import { useSelector, useDispatch } from 'react-redux'
import { setUser, initialState } from 'store/auth/userSlice'
import {
    apiGetCapabilities,
    apiSignIn,
    apiSignOut,
    apiSignUp,
    apiSwitchEntity,
} from '../api/authApi'
import {
    onEntitySelectionRequired,
    onSignInSuccess,
    onSignOutSuccess,
} from 'store/auth/sessionSlice'
import appConfig from 'shared/config/app.config'
import { REDIRECT_URL_KEY } from 'constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from 'hooks/useQuery'
import { getDefaultAuthenticatedPath } from 'utils/routeUtils'

const ENTITY_SELECTION_PATH = '/select-entity'

const FALLBACK_USER = {
    avatar: '',
    username: 'Anonymous',
    authority: [],
    email: '',
}

function useAuth() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const query = useQuery()
    const { token, signedIn, requiresEntitySelection, availableLegalEntityIds } =
        useSelector((state) => state.auth.session)

    const finalizeSignIn = async (resp) => {
        if (!resp) return
        const { token: nextToken, user } = resp
        dispatch(onSignInSuccess(nextToken))
        let capabilities = null
        try {
            capabilities = await apiGetCapabilities()
        } catch (error) {
            console.error('Failed to load capabilities', error)
        }
        if (user) {
            const nextUser = {
                ...(user || FALLBACK_USER),
                capabilities: capabilities || undefined,
                permissions:
                    capabilities?.permissions || user?.permissions || [],
                services: capabilities?.services || user?.services || [],
                features: capabilities?.features || user?.features || {},
                data_scope:
                    capabilities?.data_scope || user?.data_scope || null,
            }
            dispatch(
                setUser(nextUser)
            )
            const redirectUrl = query.get(REDIRECT_URL_KEY)
            navigate(
                redirectUrl ? redirectUrl : getDefaultAuthenticatedPath(nextUser)
            )
            return
        }
        navigate(appConfig.authenticatedEntryPath)
    }

    const signIn = async (values) => {
        try {
            const resp = await apiSignIn(values)
            if (!resp) return undefined

            // Requirement 2.10: when the user belongs to multiple
            // Legal_Entities the backend returns
            // `requires_entity_selection = true` plus the assigned
            // entity ids. Park the JWT and route to the selection
            // screen instead of finalizing the sign-in.
            if (resp.requires_entity_selection) {
                dispatch(
                    onEntitySelectionRequired({
                        token: resp.token,
                        availableLegalEntityIds:
                            resp.available_legal_entity_ids || [],
                    })
                )
                if (resp.user) {
                    dispatch(setUser(resp.user || FALLBACK_USER))
                }
                navigate(ENTITY_SELECTION_PATH)
                return {
                    status: 'entity_selection_required',
                    message: '',
                    availableLegalEntityIds:
                        resp.available_legal_entity_ids || [],
                }
            }

            await finalizeSignIn(resp)
            return {
                status: 'success',
                message: '',
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const signUp = async (values) => {
        try {
            const resp = await apiSignUp(values)
            if (resp.data) {
                const { token } = resp.data
                dispatch(onSignInSuccess(token))
                if (resp.data.user) {
                    dispatch(
                        setUser(
                            resp.data.user || FALLBACK_USER
                        )
                    )
                }
                const redirectUrl = query.get(REDIRECT_URL_KEY)
                navigate(
                    redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                )
                return {
                    status: 'success',
                    message: '',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    /**
     * Switch the active Legal_Entity context for an already-authenticated
     * user (Requirements 2.10 / 2.12). The backend returns a fresh JWT
     * scoped to the selected entity and invalidates the previous token
     * via `token_version`, so we replace the persisted token before
     * navigating into the application.
     */
    const switchEntity = async (legalEntityId) => {
        try {
            const resp = await apiSwitchEntity({
                legal_entity_id: legalEntityId,
            })
            if (!resp) return { status: 'failed', message: 'No response' }
            await finalizeSignIn(resp)
            return {
                status: 'success',
                message: '',
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const handleSignOut = () => {
        dispatch(onSignOutSuccess())
        dispatch(setUser(initialState))
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    const signOut = async () => {
        handleSignOut()
        try {
            await apiSignOut()
        } catch (error) {
            console.error(error)
        }
    }

    return {
        // Authenticated for protected-route purposes only after the
        // entity selection step is complete (Requirement 2.10). While
        // selection is pending, the JWT is held but `signedIn` is false
        // so ProtectedRoute redirects to the public side.
        authenticated: Boolean(token && signedIn),
        token,
        requiresEntitySelection,
        availableLegalEntityIds,
        signIn,
        signUp,
        signOut,
        switchEntity,
    }
}

export default useAuth
